<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faXmark, faSearch, faRotate, faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
  import Pagination from './Pagination.svelte';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  
  export let showModal = false;
  export let documentId = null;
  export let collection = null;
  export let title = "Audit Logs";
  
  const dispatch = createEventDispatcher();
  const API_BASE_URL = '/api/proxy';
  
  let auditLogs = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let isLoading = true;
  let searchTerm = '';
  let expandedRows = new Set();
  
  $: if (showModal) {
    fetchAuditLogs();
    currentPage = 1;
  }
  
  async function fetchAuditLogs(page = 1) {
    isLoading = true;
    try {
      let url = `${API_BASE_URL}/auditLogs?page=${page}&pageSize=20`;
      
      if (documentId) {
        url += `&documentId=${documentId}`;
      }
      
      if (collection) {
        url += `&collection=${collection}`;
      }
      
      if (searchTerm) {
        // url += `&searchText=${searchTerm}`;
      }
      
      const res = await fetch(url);
      
      if (!res.ok) {
        throw new Error('Failed to fetch audit logs');
      }
      
      const responseData = await res.json();
      
      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;
      
      auditLogs = responseData.data || [];
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      auditLogs = [];
    } finally {
      isLoading = false;
    }
  }
  
  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchAuditLogs(newPage);
    }
  }
  
  function handleSearch() {
    fetchAuditLogs(1);
  }
  
  function handleKeydown(event) {
    if (event.key === 'Enter') {
      handleSearch();
    }
  }
  
  function handleRefresh() {
    fetchAuditLogs(1);
  }
  
  function handleClose() {
    dispatch('close');
  }
  
  function formatDate(dateString) {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  }
  
  function truncateString(str, maxLength = 100) {
    if (!str) return '';
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
  }
  
  function toggleRow(index) {
    if (expandedRows.has(index)) {
      expandedRows.delete(index);
    } else {
      expandedRows.add(index);
    }
    expandedRows = expandedRows; // Trigger reactivity
  }
  
  function formatDocumentSummary(doc, updatedFields) {
    if (!doc && !updatedFields) return '';
    
    // If we have updated fields, show those instead
    if (updatedFields) {
      const parts = [];
      
      // Convert updatedFields to a more readable format
      for (const [key, value] of Object.entries(updatedFields)) {
        // Skip internal fields and empty values
        if (key.startsWith('_') || value === undefined || value === '') continue;
        
        // Format the value based on its type
        let displayValue = value;
        if (typeof value === 'object') {
          displayValue = JSON.stringify(value);
        }
        
        parts.push(`${key}: ${displayValue}`);
      }
      
      if (parts.length > 0) {
        return `Updated: ${parts.join(' | ')}`;
      }
    }
    
    // Fallback to the original document summary if no updated fields
    const parts = [];
    
    // Add subject ID if available
    if (doc?.subjectId) {
      parts.push(`Subject: ${doc.subjectId}`);
    }
    
    // Add site ID if available
    if (doc?.siteId) {
      parts.push(`Site: ${doc.siteId}`);
    }
    
    // Add status date if available
    if (doc?.statusDate) {
      parts.push(`Status: ${doc.statusDate}`);
    }
    
    // If we have no parts, show a generic message
    if (parts.length === 0) {
      return 'Document details available';
    }
    
    return parts.join(' | ');
  }
  
  function formatUserDisplay(email) {
    if (!email) return 'Unknown';
    if (email === 'system') return 'system';
    return email;
  }

  function getUserDisplayClass(email) {
    if (!email) return 'text-gray-500';
    if (email === 'system') return 'text-orange-600 font-semibold';
    return 'text-blue-600';
  }
</script>

{#if showModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={handleClose}></div>
    
    <div class="relative bg-white dark:bg-gray-900 w-full max-w-5xl max-h-[80vh] rounded shadow-lg flex flex-col">
      <!-- Header -->
      <div class="flex justify-between items-center px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <h2 class="text-base font-semibold text-slate-900 dark:text-white">{title}</h2>
        <button class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" on:click={handleClose}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      
      <!-- Audit Logs Table -->
      <div class="flex-grow overflow-y-auto p-3">
        {#if isLoading}
          <div class="flex justify-center items-center h-24">
            <div class="rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" style="animation: spin 1s linear infinite;"></div>
          </div>
        {:else if auditLogs.length === 0}
          <div class="text-center text-slate-400 py-4">
            {searchTerm ? 'No audit logs match your search' : 'No audit logs found'}
          </div>
        {:else}
          <div class="overflow-x-auto">
            <table class="min-w-full border-collapse table-auto">
              <thead>
                <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
                  <th class="px-4 py-2 text-left bold w-32">Collection</th>
                  <th class="px-4 py-2 text-left bold w-24">Operation</th>
                  <th class="px-4 py-2 text-left bold w-32">By</th>
                  <th class="px-4 py-2 text-left bold w-48">At</th>
                  <th class="px-4 py-2 text-left bold">Document</th>
                </tr>
              </thead>
              <tbody>
                {#each auditLogs as log, index}
                  <tr
                    class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index % 2 === 0
                      ? 'bg-stone-50 dark:bg-gray-725'
                      : 'bg-stone-75 dark:bg-gray-750'}"
                  >
                    <td class="px-4 py-2 align-top w-32">{log.collection}</td>
                    <td class="px-4 py-2 align-top w-24">{log.operationType}</td>
                    <td class="px-4 py-2 align-top w-32">{formatUserDisplay(log.lastUpdateUserEmail)}</td>
                    <td class="px-4 py-2 w-48 align-top">{formatDate(log.fullDocument?.lastUpdateDate || log.fullDocument?.updatedAt || log.triggeredAt)}</td>
                    <td class="px-4 py-2 break-all align-top">
                      <div class="text-xs">
                        <div class="flex items-start gap-2">
                          <button 
                            class="text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 flex-shrink-0 mt-1"
                            on:click={() => toggleRow(index)}
                          >
                            <FontAwesomeIcon icon={expandedRows.has(index) ? faChevronUp : faChevronDown} />
                          </button>
                          <div class="line-clamp-2 text-slate-700 dark:text-slate-300">
                            {formatDocumentSummary(log.fullDocument, log.updatedFields)}
                          </div>
                        </div>
                        {#if expandedRows.has(index)}
                          <div class="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                            {#if log.updatedFields}
                              <div class="mb-2">
                                <div class="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Updated Fields:</div>
                                <pre class="whitespace-pre-wrap text-slate-600 dark:text-slate-400">{JSON.stringify(log.updatedFields, null, 2)}</pre>
                              </div>
                              <!-- <div>
                                <div class="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Full Document:</div>
                                <pre class="whitespace-pre-wrap text-slate-600 dark:text-slate-400">{JSON.stringify(log.fullDocument, null, 2)}</pre>
                              </div> -->
                            {:else}
                              <pre class="whitespace-pre-wrap text-slate-600 dark:text-slate-400">{JSON.stringify(log.fullDocument, null, 2)}</pre>
                            {/if}
                          </div>
                        {/if}
                      </div>
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
          
          <!-- Pagination -->
          <div class="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-4">
            <div>
              <span class="text-sm text-slate-500 dark:text-slate-400">
                {resultCount} {resultCount === 1 ? 'result' : 'results'}
              </span>
            </div>
            
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}

<svelte:head>
  <style>
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  </style>
</svelte:head> 